import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Briefcase, ShieldCheck, GraduationCap, HandCoins } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto } from "@/lib/mailto";

const doelgroepen = [
  {
    icon: Building2,
    key: "gemeentelijke-professionals",
    titel: "Gemeentelijke professionals",
    kort: "Casemanagers, klantmanagers, participatiecoaches, wijkteams.",
    inhoud:
      "Een programma voor de mensen die dagelijks met langdurig vastgelopen inwoners werken. Vertrekpunt is de LevelLeven-methodiek: Startpunt, Rugzak, Rem, Vonk, Speelruimte, LevelMoment en Volgende beweging. De werkvormen — motivational interviewing, systemisch werken, transactionele analyse — zijn gereedschap dat we ónder de motorkap gebruiken.",
    onderwerpen: [
      "Effectieve gespreksvoering met langdurig vastgelopen inwoners",
      "Herkennen van belemmerende patronen en systemische lasten",
      "Aansluiten bij intrinsieke motivatie (SDT)",
    ],
  },
  {
    icon: Briefcase,
    key: "werkgevers",
    titel: "Werkgevers",
    kort: "Voor bedrijven die deelnemers uit het programma overnemen.",
    inhoud:
      "Wanneer een deelnemer uitstroomt naar werk, is het cruciaal dat de nieuwe werkomgeving weet hoe zij deze mensen duurzaam kunnen begeleiden. Wij trainen leidinggevenden en collega's om te bouwen op wat de deelnemer al heeft geleerd, niet om alles opnieuw te doen.",
    onderwerpen: [
      "Onboarding voor werknemers met een lange afstand tot de arbeidsmarkt",
      "Signalen van terugval, en hoe te reageren",
      "Buddy- en mentor-vaardigheden op de werkvloer",
    ],
  },
  {
    icon: HandCoins,
    key: "uwv",
    titel: "UWV",
    kort: "Speciaal programma voor UWV-medewerkers.",
    inhoud:
      "Voor arbeidsdeskundigen, re-integratiespecialisten en werkbegeleiders van het UWV. Focus op de doelgroep die met WW of WIA vastloopt op meerdere leefgebieden tegelijk, waar de standaard re-integratie niet volstaat.",
    onderwerpen: [
      "Multiproblematiek herkennen zonder te labelen",
      "Van diagnose naar activering, de tussenfase invullen",
      "Samenwerken met welzijnspartners en gemeente",
    ],
  },
  {
    icon: GraduationCap,
    key: "mbo",
    titel: "MBO-scholen",
    kort: "Voor mentoren, studieloopbaanbegeleiders en zorgcoördinatoren.",
    inhoud:
      "MBO-scholen zien studenten die zonder passende begeleiding uitvallen en soms rechtstreeks in de bijstand belanden. Wij trainen begeleiders om vroegtijdig te herkennen wat er speelt en gerichter te interveniëren, vóór het traject stukloopt.",
    onderwerpen: [
      "Vroegtijdig signaleren van (multi-)problematiek",
      "Gedragsverandering bij jongvolwassenen",
      "Warme overdracht naar gemeente of zorgpartner",
    ],
  },
  {
    icon: ShieldCheck,
    key: "verzekeraars",
    titel: "Verzekeraars",
    kort: "Voor case-coördinatoren en re-integratieadviseurs.",
    inhoud:
      "Bij verzuim en WGA-instroom is de mens achter het dossier vaak buiten beeld. LevelLeven biedt verzekeraars een training die case-coördinatoren helpt om écht contact te maken, patronen te doorbreken en re-integratie duurzaam te maken.",
    onderwerpen: [
      "Herstel-gericht werken zonder medicaliseren",
      "Duurzame re-integratie na langdurig verzuim",
      "Regievoering vanuit vertrouwen, niet vanuit controle",
    ],
  },
];

export default function Trainingen() {
  usePageMeta({
    title: "Trainingen, LevelLeven",
    description:
      "Trainingen op basis van de LevelLeven-methodiek voor gemeentelijke professionals, werkgevers, UWV, mbo-scholen en verzekeraars.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl reveal">
            <SectionLabel>Trainingen</SectionLabel>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1F5132] font-light">
              De methodiek,{" "}
              <span className="italic text-[#E06B3F]">ook voor wie er omheen staat.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#1F5132] leading-relaxed font-light">
              LevelLeven heeft een eigen methodiek — zeven kernbegrippen die
              deelnemers doorlopen. In onze trainingen leren professionals die
              methodiek herkennen, gebruiken en er warm op aansluiten.
              De onderliggende technieken (MI, systemisch werken, TA)
              blijven onder de motorkap: gereedschap, geen merk.
            </p>
          </div>
        </div>
      </section>

      {/* 5 DOELGROEPEN */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 space-y-4">
          {doelgroepen.map((d, i) => {
            const Icon = d.icon;
            return (
              <article
                key={d.key}
                id={d.key}
                data-testid={`training-${d.key}`}
                className="reveal grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 bg-[#EFECE5] rounded-3xl border border-[#E5E1D8] p-8 sm:p-10 hover:border-[#E06B3F] transition-colors scroll-mt-28"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="lg:col-span-4">
                  <div className="w-12 h-12 rounded-full bg-[#E06B3F]/10 text-[#E06B3F] flex items-center justify-center mb-5">
                    <Icon size={20} />
                  </div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-2">
                    Training {String(i + 1).padStart(2, "0")}
                  </p>
                  <Link to={`/trainingen/${d.key}`} data-testid={`training-detail-${d.key}`} className="block group">
                    <h2 className="font-heading text-3xl sm:text-4xl text-[#1D2A22] leading-tight font-light group-hover:text-[#E06B3F] transition-colors">
                      {d.titel}
                    </h2>
                  </Link>
                  <p className="mt-3 text-[#7A8276] italic text-sm">{d.kort}</p>

                  {/* Praktische info */}
                  <dl className="mt-6 space-y-3 text-sm">
                    <div className="flex justify-between border-t border-[#E5E1D8] pt-3">
                      <dt className="text-[#7A8276]">Duur</dt>
                      <dd className="text-[#1D2A22]">2 dagen (of 4 dagdelen)</dd>
                    </div>
                    <div className="flex justify-between border-t border-[#E5E1D8] pt-3">
                      <dt className="text-[#7A8276]">Groepsgrootte</dt>
                      <dd className="text-[#1D2A22]">8-12 deelnemers</dd>
                    </div>
                    <div className="flex justify-between border-t border-[#E5E1D8] pt-3">
                      <dt className="text-[#7A8276]">Data</dt>
                      <dd className="text-[#1D2A22]">Op afspraak · start binnen 6 weken</dd>
                    </div>
                    <div className="flex justify-between border-t border-[#E5E1D8] pt-3">
                      <dt className="text-[#7A8276]">Certificering</dt>
                      <dd className="text-[#1D2A22]">Ja, na afronding</dd>
                    </div>
                  </dl>
                </div>
                <div className="lg:col-span-8">
                  <p className="text-[#4A5D4E] leading-relaxed font-light">
                    {d.inhoud}
                  </p>
                  <p className="mt-6 text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-3">
                    Wat leert u concreet?
                  </p>
                  <ul className="space-y-2.5">
                    {d.onderwerpen.map((o, oi) => (
                      <li key={oi} className="flex items-baseline gap-3 text-[#1D2A22]">
                        <span className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] w-6 shrink-0">
                          {String(oi + 1).padStart(2, "0")}
                        </span>
                        {o}
                      </li>
                    ))}
                  </ul>

                  {/* CTA rij per training */}
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to={`/trainingen/${d.key}`}
                      data-testid={`training-more-${d.key}`}
                      className="group inline-flex items-center gap-3 bg-[#1D2A22] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-5 pr-2 py-2 transition-colors text-sm"
                    >
                      <span>Bekijk alle informatie</span>
                      <span className="bg-[#E06B3F] group-hover:bg-[#FAF7F1] group-hover:text-[#1D2A22] text-white w-8 h-8 rounded-full flex items-center justify-center transition-colors">
                        <ArrowUpRight size={14} />
                      </span>
                    </Link>
                    <a
                      href={buildPilotMailto({
                        subject: `Aanmelden training, ${d.titel}`,
                        body: `Beste LevelLeven,\n\nWij willen ons aanmelden voor de training "${d.titel}".\n\nOrganisatie:\nAantal deelnemers:\nContactpersoon + telefoon:\nGewenste startdatum:\n\nMet vriendelijke groet,`,
                      })}
                      className="inline-flex items-center gap-2 rounded-full border border-[#1D2A22] text-[#1D2A22] hover:bg-[#1D2A22] hover:text-[#FAF7F1] px-5 py-2 text-sm transition-colors"
                    >
                      Direct aanmelden
                    </a>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="rounded-3xl bg-[#1D2A22] text-[#FAF7F1] px-8 sm:px-16 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-4">
                Trainingen op maat
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Kennisdeling die{" "}
                <span className="italic text-[#D19B72]">écht landt</span>.
              </h2>
              <p className="mt-6 text-[#FAF7F1]/70 max-w-lg">
                Vraag een programma op maat aan. Wij denken graag mee over
                context, deelnemersgroep en gewenste diepgang.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <a
                href={buildPilotMailto({
                  subject: "Aanvraag training LevelLeven",
                  body:
                    "Beste LevelLeven,\n\nWij zijn geïnteresseerd in een training.\n\nOrganisatie:\nDoelgroep (bijv. casemanagers, UWV, werkgever):\nAantal deelnemers:\nContactpersoon + telefoon:\n\nMet vriendelijke groet,",
                })}
                className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1D2A22] text-white rounded-full pl-7 pr-2 py-2 transition-colors duration-300"
              >
                <span className="text-base font-medium">Vraag een training aan</span>
                <span className="bg-[#FAF7F1] text-[#1D2A22] group-hover:bg-[#E06B3F] group-hover:text-white w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                  <ArrowUpRight size={20} />
                </span>
              </a>
              <Link
                to="/contact"
                className="mt-3 inline-flex items-center gap-2 text-sm text-[#FAF7F1]/70 hover:text-[#FAF7F1] link-underline"
              >
                Of via het contactformulier
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
